/**
 * Collection Section JavaScript
 * Handles sorting, filtering with Search & Discovery API
 */

class CollectionSection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;

    // Sort elements
    this.sortTrigger = section.querySelector('.collection__sort-trigger');
    this.sortDropdown = section.querySelector('.collection__sort-dropdown');
    this.sortOptions = section.querySelectorAll('.collection__sort-option');

    // Filter elements
    this.filterTrigger = section.querySelector('.collection__filter-trigger');
    this.filterDrawer = section.querySelector('.collection__filter-drawer');
    this.filterClose = section.querySelector('.collection__filter-close');
    this.filterCloseText = section.querySelector('.collection__filter-close-text');
    this.filterOverlay = section.querySelector('.collection__filter-overlay');
    this.filterForm = section.querySelector('.collection__filter-form');
    this.filterGroupTriggers = section.querySelectorAll('.collection__filter-group-trigger');
    this.filterCheckboxes = section.querySelectorAll('.collection__filter-checkbox');
    this.filterRadios = section.querySelectorAll('.collection__filter-radio');

    // Handle simple color param from megamenu
    this.handleSimpleColorParam();

    this.bindEvents();
  }

  handleSimpleColorParam() {
    const url = new URL(window.location.href);
    const colorParam = url.searchParams.get('color');

    if (!colorParam) return;

    // Find checkbox with matching color label
    const matchingCheckbox = this.section.querySelector(
      `.collection__filter-checkbox[data-color-label="${colorParam}"]`
    );

    if (matchingCheckbox && matchingCheckbox.name && matchingCheckbox.value) {
      // Build new URL with correct Shopify filter format
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('color');
      newUrl.searchParams.set(matchingCheckbox.name, matchingCheckbox.value);

      // Redirect to correct filter URL
      window.location.replace(newUrl.toString());
    }
  }

  bindEvents() {
    // Sort events
    if (this.sortTrigger) {
      this.sortTrigger.addEventListener('click', () => this.toggleSort());
      document.addEventListener('click', (e) => this.handleOutsideClick(e));
    }

    if (this.sortOptions) {
      this.sortOptions.forEach(option => {
        option.addEventListener('click', () => this.handleSort(option));
      });
    }

    // Filter events
    if (this.filterTrigger) {
      this.filterTrigger.addEventListener('click', () => this.openFilterDrawer());
    }

    if (this.filterClose) {
      this.filterClose.addEventListener('click', () => this.closeFilterDrawer());
    }

    if (this.filterCloseText) {
      this.filterCloseText.addEventListener('click', () => this.closeFilterDrawer());
    }

    if (this.filterOverlay) {
      this.filterOverlay.addEventListener('click', () => this.closeFilterDrawer());
    }

    if (this.filterGroupTriggers) {
      this.filterGroupTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => this.toggleFilterGroup(trigger));
      });
    }

    // Auto-submit on checkbox change
    if (this.filterCheckboxes) {
      this.filterCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => this.handleFilterChange());
      });
    }

    // Handle price range radio buttons
    if (this.filterRadios) {
      this.filterRadios.forEach(radio => {
        radio.addEventListener('change', () => this.handlePriceFilterChange(radio));
      });
    }

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeSort();
        this.closeFilterDrawer();
      }
    });
  }

  // Sort methods
  toggleSort() {
    const isExpanded = this.sortTrigger.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.closeSort();
    } else {
      this.openSort();
    }
  }

  openSort() {
    this.sortTrigger.setAttribute('aria-expanded', 'true');
    this.sortDropdown.setAttribute('aria-hidden', 'false');
  }

  closeSort() {
    if (this.sortTrigger) {
      this.sortTrigger.setAttribute('aria-expanded', 'false');
    }
    if (this.sortDropdown) {
      this.sortDropdown.setAttribute('aria-hidden', 'true');
    }
  }

  handleOutsideClick(e) {
    if (!this.sortTrigger || !this.sortDropdown) return;

    if (!this.sortTrigger.contains(e.target) && !this.sortDropdown.contains(e.target)) {
      this.closeSort();
    }
  }

  handleSort(option) {
    const sortValue = option.dataset.sortValue;
    const url = new URL(window.location.href);

    url.searchParams.set('sort_by', sortValue);

    window.location.href = url.toString();
  }

  // Filter methods
  openFilterDrawer() {
    this.filterDrawer.setAttribute('aria-hidden', 'false');
    this.filterTrigger.setAttribute('aria-expanded', 'true');
    this.filterOverlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  closeFilterDrawer() {
    if (this.filterDrawer) {
      this.filterDrawer.setAttribute('aria-hidden', 'true');
    }
    if (this.filterTrigger) {
      this.filterTrigger.setAttribute('aria-expanded', 'false');
    }
    if (this.filterOverlay) {
      this.filterOverlay.classList.remove('is-active');
    }
    document.body.style.overflow = '';
  }

  toggleFilterGroup(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', !isExpanded);
  }

  handleFilterChange() {
    // Build URL with selected filters
    const url = new URL(window.location.href);

    // Clear existing filter params (except price)
    const keysToRemove = [];
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.') && !key.includes('price')) {
        keysToRemove.push(key);
      }
    });
    keysToRemove.forEach(key => url.searchParams.delete(key));

    // Add checked filters
    this.filterCheckboxes.forEach(checkbox => {
      if (checkbox.checked && checkbox.name) {
        url.searchParams.append(checkbox.name, checkbox.value);
      }
    });

    window.location.href = url.toString();
  }

  handlePriceFilterChange(radio) {
    const url = new URL(window.location.href);
    const minParam = radio.dataset.minParam;
    const maxParam = radio.dataset.maxParam;
    const minValue = radio.dataset.min;
    const maxValue = radio.dataset.max;

    // Remove existing price filters
    url.searchParams.delete(minParam);
    url.searchParams.delete(maxParam);

    // Add new price filters
    if (minValue) {
      url.searchParams.set(minParam, minValue);
    }
    if (maxValue) {
      url.searchParams.set(maxParam, maxValue);
    }

    window.location.href = url.toString();
  }
}

// Initialize all collection sections
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('[data-section-type="w-collection"]');
  sections.forEach(section => new CollectionSection(section));
});
